<script setup>
import { ref, watch, computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import FilterSelect from '../../socialMonitoring/components/FilterSelect.vue';
import { WORKER_ROLES } from '../utils/workerMeta';

const props = defineProps({
  open: { type: Boolean, default: false },
  worker: { type: Object, default: null },
  leaders: { type: Array, default: () => [] },
  saving: { type: Boolean, default: false },
});
const emit = defineEmits(['close', 'save', 'delete']);

const form = ref({
  name: '',
  phone: '',
  email: '',
  role: 'cabo',
  city: '',
  state: '',
  neighborhood: '',
  estimatedVotes: 0,
  confirmedVotes: 0,
  leaderId: '',
  notes: '',
});

const isEdit = computed(() => !!props.worker?.id);
const roleOptions = WORKER_ROLES.map(r => ({
  value: r.value,
  label: r.label,
  dot: r.dot,
}));
const leaderOptions = computed(() => [
  { value: '', label: 'Sem líder' },
  ...props.leaders.map(l => ({
    value: String(l.id),
    label: l.name,
    hint: l.city,
  })),
]);

watch(
  () => [props.open, props.worker],
  () => {
    if (!props.open) return;
    if (props.worker) {
      form.value = {
        name: props.worker.name,
        phone: props.worker.phone || '',
        email: props.worker.email || '',
        role: props.worker.role,
        city: props.worker.city || '',
        state: props.worker.state || '',
        neighborhood: props.worker.neighborhood || '',
        estimatedVotes: props.worker.estimatedVotes || 0,
        confirmedVotes: props.worker.confirmedVotes || 0,
        leaderId: props.worker.leaderId ? String(props.worker.leaderId) : '',
        notes: props.worker.notes || '',
      };
    } else {
      form.value = {
        name: '',
        phone: '',
        email: '',
        role: 'cabo',
        city: '',
        state: '',
        neighborhood: '',
        estimatedVotes: 0,
        confirmedVotes: 0,
        leaderId: '',
        notes: '',
      };
    }
  },
  { immediate: true }
);

function submit() {
  const payload = { ...form.value };
  payload.leaderId = payload.leaderId ? Number(payload.leaderId) : null;
  if (props.worker?.id) payload.id = props.worker.id;
  emit('save', payload);
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      @click.self="emit('close')"
    >
      <div
        class="absolute inset-0 bg-black/50 backdrop-blur-sm"
        @click="emit('close')"
      />
      <div
        class="relative z-10 flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-n-background shadow-2xl"
      >
        <div
          class="flex items-center justify-between border-b border-n-weak px-5 py-3"
        >
          <h3 class="text-base font-bold text-n-slate-12">
            {{ isEdit ? 'Editar cabo' : 'Novo cabo eleitoral' }}
          </h3>
          <Button
            variant="ghost"
            size="sm"
            icon="i-lucide-x"
            @click="emit('close')"
          />
        </div>

        <div class="flex-1 overflow-y-auto p-5">
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div class="md:col-span-2">
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Nome</label>
              <input
                v-model="form.name"
                type="text"
                class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Papel</label>
              <FilterSelect
                v-model="form.role"
                :options="roleOptions"
                min-width="100%"
              />
            </div>
            <div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Líder</label>
              <FilterSelect
                v-model="form.leaderId"
                :options="leaderOptions"
                min-width="100%"
              />
            </div>
            <div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Telefone</label>
              <input
                v-model="form.phone"
                type="text"
                class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">E-mail</label>
              <input
                v-model="form.email"
                type="email"
                class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Cidade</label>
              <input
                v-model="form.city"
                type="text"
                class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">UF</label>
              <input
                v-model="form.state"
                type="text"
                maxlength="2"
                class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm uppercase"
              />
            </div>
            <div class="md:col-span-2">
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Bairro</label>
              <input
                v-model="form.neighborhood"
                type="text"
                class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Votos estimados</label>
              <input
                v-model.number="form.estimatedVotes"
                type="number"
                min="0"
                class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Votos confirmados</label>
              <input
                v-model.number="form.confirmedVotes"
                type="number"
                min="0"
                class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm"
              />
            </div>
            <div class="md:col-span-2">
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Observações</label>
              <textarea
                v-model="form.notes"
                rows="2"
                class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm"
              />
            </div>
          </div>
        </div>

        <div
          class="flex items-center justify-between border-t border-n-weak px-5 py-3"
        >
          <Button
            v-if="isEdit"
            variant="ghost"
            size="sm"
            icon="i-lucide-trash-2"
            color="ruby"
            @click="emit('delete', worker)"
          >
            Excluir
          </Button>
          <span v-else />
          <div class="flex gap-2">
            <Button variant="outline" size="sm" @click="emit('close')">
              Cancelar
            </Button>
            <Button
              variant="solid"
              color="blue"
              size="sm"
              :is-loading="saving"
              :disabled="!form.name.trim()"
              @click="submit"
            >
              {{ isEdit ? 'Salvar' : 'Criar' }}
            </Button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
